import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { DataService } from '../../services/data-service';
import Loader from 'src/components/Loader';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import StatWidget from 'src/components/widgets/common/StatWidget';
import { Delete, SpeakerGroupOutlined } from '@mui/icons-material';
import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { Client, ClientApiResponse, ClientsApiResponse } from 'src/types/client.types';
import ClientDrawer from './ClientDrawer';
import { Box, Paper } from '@mui/material';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { showAlert } from 'src/redux/alert/alertReducer';

const ClientListing: React.FC = () => {
  const [Clients, setClients] = useState<Record<string, Client>>({});
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [updateData, setUpdateData] = useState<Client | undefined>(undefined);
  const userRole = useAppSelector(s => s.user.user.role) as ROLE;
  const dispatch = useAppDispatch();

  const widgetVisibility = {
    totalClients: false,
  };

  // Fetch Clients
  const initialize = async () => {
    setLoading(true);
    try {
      const res = await DataService.get('api/Client');
      const response: ClientsApiResponse = await res.json();
      const data: ClientApiResponse[] = response.data || [];
      const result: Record<string, Client> = {};
      data.forEach((w) => {
        result[w.id] = {
          Id: w.id,
          Name: w.name,
          Email: w.email,
        };
      });
      setClients(result);
    } catch (e) {
      toast.error('Failed to fetch Clients');
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);


  // Export handler
  const handleExport = async () => {
    try {
      const response = await DataService.get('api/Client/export', {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Clients_export.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Export failed');
      }
    } catch {
      toast.error('Export failed');
    }
  };

  // Table headers
  const headers: EnhancedTableColumn[] = [
    {
      id: 'Name',
      label: 'Name',
      type: EnhancedTableColumnType.TEXT,
    },
    {
      id: 'Email',
      label: 'Email',
      type: EnhancedTableColumnType.TEXT,
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: (id: string) => {
            setUpdateData(Clients[id]);
            setOpenDrawer(true);
          },
          icon: <EditIcon fontSize="small" />,
          label: 'Edit',
          visible: () => PermissionService.getInstance().isAllowed(ACTION.EDIT_CLIENT, userRole),
        },
        {
          onClick: (id: string) => {
            dispatch(OpenConfirmation({
              open: true,
              title: 'Delete Client',
              message: 'Are you sure you want to delete this client?',
              onSubmit: async () => {
                try {
                  const response = await DataService.delete(`api/Client/${id}`, {});
                  const data = await response.json();

                  if (response.ok && (data.Success === true || data.success === true)) {
                    dispatch(showAlert({ message: 'Client deleted successfully', type: 'success' }));
                    initialize();
                  } else {
                    const errorMessage = data.Message || data.message || 'Failed to delete client';
                    dispatch(showAlert({ message: errorMessage, type: 'error' }));
                  }
                } catch (error) {
                  dispatch(showAlert({ message: 'Failed to delete client', type: 'error' }));
                }
              }
            }));
          },
          icon: <Delete fontSize="small" />,
          label: 'Delete',
          visible: () => PermissionService.getInstance().isAllowed(ACTION.VIEW_ALL_CLIENTS, userRole),
        }
      ]
    }
  ];

  // Table actions
  const actions: ITableMenuAction[] = [
    {
      key: 'add',
      title: 'Add Client',
      icon: <AddIcon />,
      disabled: (selected: string[]) => false,
      onClick: () => {
        setUpdateData(undefined);
        setOpenDrawer(true);
      },
      visible: () => PermissionService.getInstance().isAllowed(ACTION.CREATE_CLIENT, userRole),
    },
    {
      key: 'export',
      title: '',
      icon: <DownloadIcon />,
      disabled: (selected: string[]) => false,
      onClick: handleExport,
    }
  ];

  // Prepare data for EnhancedTable
  const dataObj: Record<string, any> = Object.fromEntries(
    Object.entries(Clients).map(([id, Client]) => [
      id,
      {
        ...Client,
      }
    ])
  );


  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Loader/>
      </Box>
    );
  }

  return (
    <Box>
      <title>Clients</title>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {widgetVisibility.totalClients && (
          <StatWidget
            title="Total Clients"
            value={Object.keys(Clients).length}
            icon={<SpeakerGroupOutlined />}
            color="#1976d2"
            visible={widgetVisibility.totalClients}
          />
        )}
      </div>

      {/* Main Content */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <EnhancedTable
          title="Clients"
          columns={headers}
          data={dataObj}
          actions={actions}
          collapsible={true}
          striped={true}
          boxedCells={true}
          wmsMode={true}
        />
      </Paper>

      {/* Client Drawer */}
      {openDrawer && (
        <ClientDrawer
          open={openDrawer}
          onClose={() => {
            setOpenDrawer(false);
            setUpdateData(undefined);
          }}
          onSuccess={() => {
            setOpenDrawer(false);
            setUpdateData(undefined);
            initialize();
          }}
          updateData={updateData}
        />
      )}
    </Box>
  );
};

export default ClientListing;
